import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";

function AudienceBlock({ id, tone, eyebrow, title, body, bullets, ctaHref, ctaText, testId }) {
  return (
    <section id={id} className={`py-16 lg:py-20 ${tone === "dark" ? "" : "bg-cream"}`} data-testid={testId}
              style={tone === "dark" ? { background: "linear-gradient(180deg, #0F3D2E 0%, #16305c 100%)", color: "#f6f4ef" } : undefined}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-10 items-start">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] mb-3 text-gold">{eyebrow}</div>
          <h2 className={`font-heading text-3xl sm:text-4xl lg:text-[42px] leading-tight ${tone === "dark" ? "text-cream" : "text-ink"}`}>{title}</h2>
          <p className={`mt-4 max-w-xl ${tone === "dark" ? "" : "text-ink-soft"}`} style={tone === "dark" ? { color: "rgba(246,244,239,0.8)" } : undefined}>
            {body}
          </p>
          <Link
            to={ctaHref}
            className={`mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md font-heading text-sm transition-colors ${tone === "dark" ? "bg-gold text-navy hover:bg-gold/90" : "bg-navy text-cream hover:bg-[#256E58]"}`}
            data-testid={`${testId}-cta`}
          >
            {ctaText} <ArrowRight size={14} />
          </Link>
        </div>
        <ul className={`rounded-lg p-6 border ${tone === "dark" ? "border-white/10 bg-white/[0.04]" : "border-navy/10 bg-white"}`}>
          {bullets.map((b) => (
            <li key={b} className={`flex items-center gap-2 py-2 text-sm ${tone === "dark" ? "text-cream/90" : "text-ink"}`}>
              <CheckCircle2 size={14} className="text-gold flex-shrink-0" /> {b}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default function AudienceBlocks() {
  const startupBullets = [
    "Build your Startup Passport",
    "Add company and financial data",
    "Upload key documents",
    "Receive readiness insights",
    "Improve gaps before investor review",
    "Get discovered by relevant investors",
    "Move serious interest into deal rooms",
  ];
  const investorBullets = [
    "Explore verified opportunity cards",
    "View Trust Score and approved summaries",
    "Filter by mandate fit",
    "Express interest through a controlled workflow",
    "Request Health Check or DD access",
    "Ask structured questions",
    "Enter secure deal rooms",
  ];

  return (
    <>
      <AudienceBlock
        tone="light"
        eyebrow="FOR STARTUPS"
        title="Become investor-ready before you meet investors."
        body="VENTURAZ helps startups create a verified capital profile, identify readiness gaps, improve documentation, and present investor-approved information with confidence."
        bullets={startupBullets}
        ctaHref="/startup/apply"
        ctaText="Apply for Funding"
        testId="audience-startups"
      />
      <AudienceBlock
        tone="dark"
        eyebrow="FOR INVESTORS"
        title="Discover opportunities with context, not just pitch decks."
        body="VENTURAZ gives investors curated opportunities with verification badges, Trust Scores, financial confidence labels, health summaries, mandate-fit scores, and secure deal rooms."
        bullets={investorBullets}
        ctaHref="/investor/join"
        ctaText="Join as Investor"
        testId="audience-investors"
      />
    </>
  );
}
